import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { lessonAPI, calendarAPI } from '../../services';
import { LessonApplicationRequest } from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollToTop, usePhoneNumber } from '../../hooks';
import toast from 'react-hot-toast';
import { CustomSelect } from '../../components';
import { replaceWeekdaysInString, validateBirthDate, validateEmail, isUnder14 } from '../../utils';
import { WEEKDAY_TO_NUMBER, TIME_SLOTS } from '../../config/constants';

const LessonRequest: React.FC = () => {
  const { t } = useLanguage();

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<LessonApplicationRequest>({
    name: '',
    birthDate: '',
    gender: 'male',
    phoneNumber: '',
    email: '',
    preferredTime: '',
    lessonType: '일반',
    message: '',
    isForeigner: false,
  });

  // 전화번호 훅 사용
  const { phoneNumber, handlePhoneChange, getCleanPhoneNumber } = usePhoneNumber();

  const [selectedTimes, setSelectedTimes] = useState<{ [key: string]: string[] }>({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  });
  const [selectedWeekday, setSelectedWeekday] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyTimes, setBusyTimes] = useState<{ [key: string]: Array<{ startTime: string; endTime: string; studentName?: string; type: string }> }>({});
  const [selectedTeacher] = useState<string>('1'); // 현재 선생님 한 명뿐
  const [birthDateError, setBirthDateError] = useState<string>('');
  const [isPolicyAgreed, setIsPolicyAgreed] = useState<boolean>(false);
  const [isGuardianAgreed, setIsGuardianAgreed] = useState<boolean>(false);

  // 만 14세 미만이면 법정대리인(보호자) 동의 필요
  const requiresGuardianConsent = isUnder14(formData.birthDate);

  const weekdays = [
    { key: 'monday', label: t('weekday_monday') },
    { key: 'tuesday', label: t('weekday_tuesday') },
    { key: 'wednesday', label: t('weekday_wednesday') },
    { key: 'thursday', label: t('weekday_thursday') },
    { key: 'friday', label: t('weekday_friday') }
  ];


  // 선생님 바쁜 시간대 조회
  const loadTeacherBusyTimes = async (weekday: string) => {
    if (!selectedTeacher) {
      return;
    }

    try {
      const today = new Date();
      const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const response = await calendarAPI.getUnifiedCalendar({
        startDate: today.toISOString().split('T')[0],
        endDate: thirtyDaysLater.toISOString().split('T')[0],
        teacherId: parseInt(selectedTeacher),
        includeRegular: true,
        includeModifications: false // 비회원은 원본 정규수업만 확인
      });

      const eventsByDate = response.data.data.events || [];

      // events 배열의 각 날짜별 데이터에서 schedules를 추출
      const allSchedules: any[] = [];
      eventsByDate.forEach((dayEvent: any) => {
        if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
          dayEvent.schedules.forEach((schedule: any) => {
            allSchedules.push({
              ...schedule,
              date: dayEvent.date
            });
          });
        }
      });

      // 해당 요일의 이벤트만 필터링
      const weekdayNumber = WEEKDAY_TO_NUMBER[weekday as keyof typeof WEEKDAY_TO_NUMBER];
      const allBusyTimes = allSchedules
        .filter((event: any) => {
          // dayOfWeek로 필터링
          if (event.dayOfWeek) {
            const eventWeekdayNumber = WEEKDAY_TO_NUMBER[event.dayOfWeek as keyof typeof WEEKDAY_TO_NUMBER];
            return eventWeekdayNumber === weekdayNumber;
          }
          // 날짜로 필터링 (fallback)
          if (event.date) {
            const eventDate = new Date(event.date);
            return eventDate.getDay() === weekdayNumber;
          }
          return false;
        })
        .map((event: any) => ({
          startTime: event.startTime.substring(0, 5), // HH:MM:SS -> HH:MM
          endTime: event.endTime.substring(0, 5),
          studentName: event.studentName || event.title || t('busy_time'),
          type: event.scheduleType
        }));

      setBusyTimes(prev => ({
        ...prev,
        [weekday]: allBusyTimes
      }));
    } catch {
      // 선생님 일정 조회 오류
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // 전화번호는 별도 훅에서 처리
    if (name === 'phoneNumber') return;

    // 생년월일 유효성 검증
    if (name === 'birthDate') {
      const validation = validateBirthDate(value);
      setBirthDateError(validation.isValid ? '' : validation.errorMessage || '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWeekdaySelection = async (weekday: string) => {
    const newSelectedWeekday = selectedWeekday === weekday ? null : weekday;
    setSelectedWeekday(newSelectedWeekday);

    // 요일 선택 시 해당 요일의 선생님 바쁜 시간대 조회
    if (newSelectedWeekday) {
      await loadTeacherBusyTimes(newSelectedWeekday);
    }
  };

  const handleTimeSelection = (day: string, time: string) => {
    setSelectedTimes(prev => {
      const dayTimes = prev[day] || [];
      const isSelected = dayTimes.includes(time);

      if (isSelected) {
        return {
          ...prev,
          [day]: dayTimes.filter(t => t !== time)
        };
      } else {
        return {
          ...prev,
          [day]: [...dayTimes, time].sort()
        };
      }
    });
  };

  // 시간이 바쁜 시간대인지 확인
  const isTimeSlotBusy = (time: string, weekday: string) => {
    const weekdayBusyTimes = busyTimes[weekday] || [];
    const [hour, minute] = time.split(':').map(Number);
    const timeInMinutes = hour * 60 + minute;

    return weekdayBusyTimes.some(busyTime => {
      const [startHour, startMin] = busyTime.startTime.split(':').map(Number);
      const [endHour, endMin] = busyTime.endTime.split(':').map(Number);
      const startInMinutes = startHour * 60 + (startMin || 0);
      const endInMinutes = endHour * 60 + (endMin || 0);
      return timeInMinutes >= startInMinutes && timeInMinutes < endInMinutes;
    });
  };

  // 다음 30분이 비어있어서 합칠 수 있는지 확인
  const canMergeWithNext = (time: string, weekday: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const nextMinutes = hour * 60 + minute + 30;
    const nextTime = `${String(Math.floor(nextMinutes / 60)).padStart(2, '0')}:${String(nextMinutes % 60).padStart(2, '0')}`;
    // 다음 30분이 바쁘지 않으면 1시간 수업 가능
    return !isTimeSlotBusy(nextTime, weekday);
  };

  // 마지막 슬롯인지 확인 (21:30, 22:00은 선택 불가 - 1시간 수업이 22시를 넘어감)
  const isLastSlot = (time: string): boolean => {
    const [hour, minute] = time.split(':').map(Number);
    const timeMinutes = hour * 60 + minute;
    // 21:30 이후는 선택 불가 (21:00까지만 선택 가능)
    return timeMinutes >= 21 * 60 + 30;
  };

  const formatSelectedTimes = () => {
    const timeEntries = Object.entries(selectedTimes).filter(([_, times]) => times.length > 0);
    return timeEntries.map(([day, times]) => {
      // 영어 요일 키를 사용하여 표시
      const dayKey = day.charAt(0).toUpperCase() + day.slice(1); // 첫 글자만 대문자로
      return `${dayKey}: ${times.join(', ')}`;
    }).join(' | ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // 이메일 유효성 검증
    const emailValidation = validateEmail(formData.email || '');
    if (!emailValidation.isValid) {
      setIsSubmitting(false);
      toast.error(emailValidation.errorMessage);
      return;
    }

    // 생년월일 유효성 검증
    const birthDateValidation = validateBirthDate(formData.birthDate);
    if (!birthDateValidation.isValid) {
      setBirthDateError(birthDateValidation.errorMessage || '');
      setIsSubmitting(false);
      toast.error(birthDateValidation.errorMessage || t('check_birth_date'));
      return;
    }

    // 만 14세 미만은 법정대리인(보호자) 동의 필수
    if (requiresGuardianConsent && !isGuardianAgreed) {
      setIsSubmitting(false);
      toast.error(t('guardian_consent_required'));
      return;
    }

    try {
      const submissionData = {
        ...formData,
        phoneNumber: getCleanPhoneNumber(),
        preferredTime: replaceWeekdaysInString(formatSelectedTimes())
      };

      const response = await lessonAPI.apply(submissionData);
      if (response.success) {
        setFormData({
          name: '',
          birthDate: '',
          gender: 'male',
          phoneNumber: '',
          email: '',
          preferredTime: '',
          lessonType: '일반',
          message: '',
          isForeigner: false,
        });
        // 전화번호 훅 초기화
        handlePhoneChange('');
        setSelectedTimes({
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        });
        setSelectedWeekday(null);
        setBirthDateError('');
        setIsPolicyAgreed(false);

        // 성공 메시지 토스트 표시
        toast.success(t('lesson_apply_success'));

        // 맨 위로 스크롤
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errorMessage = response.message || t('lesson_apply_error');
        toast.error(errorMessage);
      }
    } catch (error: any) {

      // API 에러 응답에서 메시지 추출
      let errorMessage = t('lesson_apply_error');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const lessonType = searchParams.get('type');
    if (lessonType === '일반') {
      setFormData(prev => ({
        ...prev,
        lessonType: lessonType
      }));
    }
  }, [searchParams]);

  return (
    <div className="lesson-request-page">
      <div className="page-content">
        <div className="container">

          <div className="lr-layout">
            <form onSubmit={handleSubmit} className="application-form lr-form-panel">
              <div className="lr-panel-heading">
                <h2 className="lr-panel-title">{t('lr_form_title')}</h2>
                <p className="lr-panel-subtitle">{t('lr_form_subtitle')}</p>
              </div>

              {/* 모바일 전용 수업료 요약 (시안 2a) */}
              <div className="lr-pricing-strip">
                <div className="strip-info">
                  <div className="strip-name">{t('home_program_regular_title')}</div>
                  <div className="strip-meta">{t('lr_pricing_meta')}</div>
                </div>
                <div className="strip-price">{t('home_program_regular_price')}</div>
              </div>
              {/* STEP 1: 기본 정보 */}
              <div className="form-section" data-step="1">
                <div className="section-header">
                  <div className="section-label">STEP 1</div>
                  <h2 className="section-title">{t('basic_info')}</h2>
                  <p className="section-description">{t('step1_description')}</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">{t('name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder={t('name_placeholder')}
                    />
                  </div>

                  <div className="form-group">
                    <div className="label-row">
                      <label htmlFor="birthDate" className="form-label">{t('birth_date')}</label>
                      {birthDateError && (
                        <small className="form-error-inline">{birthDateError}</small>
                      )}
                    </div>
                    <input
                      type="text"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      className={`form-input ${birthDateError ? 'error' : ''}`}
                      placeholder="YYYYMMDD"
                      maxLength={8}
                      pattern="[0-9]{8}"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('gender')}</label>
                    <div className="seg-toggle">
                      <button
                        type="button"
                        className={formData.gender === 'male' ? 'on' : ''}
                        onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
                      >
                        {t('male')}
                      </button>
                      <button
                        type="button"
                        className={formData.gender === 'female' ? 'on' : ''}
                        onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
                      >
                        {t('female')}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber" className="form-label">{t('phone')}</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      required
                      className="form-input"
                      placeholder={t('phone_placeholder_no_hyphen')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">{t('email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label unrequired">{t('foreigner_status')}</label>
                  <div className="seg-toggle">
                    <button
                      type="button"
                      className={!formData.isForeigner ? 'on' : ''}
                      onClick={() => setFormData(prev => ({ ...prev, isForeigner: false }))}
                    >
                      {t('domestic_label')}
                    </button>
                    <button
                      type="button"
                      className={formData.isForeigner ? 'on' : ''}
                      onClick={() => setFormData(prev => ({ ...prev, isForeigner: true }))}
                    >
                      {t('foreigner_label')}
                    </button>
                  </div>
                </div>
              </div>

              {/* STEP 2: 수업 정보 */}
              <div className="form-section" data-step="2">
                <div className="section-header">
                  <div className="section-label">STEP 2</div>
                  <h2 className="section-title">{t('step_lesson_info')}</h2>
                  <p className="section-description">{t('step2_description')}</p>
                </div>

                <div className="form-group">
                  <label htmlFor="lessonType" className="form-label">{t('lesson_type')}</label>
                  <CustomSelect
                    id="lessonType"
                    name="lessonType"
                    value={formData.lessonType}
                    onChange={(value) => setFormData(prev => ({ ...prev, lessonType: value as "일반" }))}
                    required
                    options={[
                      { value: "일반", label: t('lesson_type_basic') }
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('preferred_time')}</label>
                  <p className="form-help-text" style={{ marginBottom: '30px' }}>
                    {t('time_help')}
                  </p>

                  <div className="weekday-selection">
                    <h4>{t('weekday_selection')}</h4>
                    <div className="weekday-buttons">
                      {weekdays.map(({ key, label }) => {
                        const hasSelectedTimes = selectedTimes[key] && selectedTimes[key].length > 0;
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`weekday-btn ${selectedWeekday === key ? 'active' : ''} ${hasSelectedTimes ? 'has-times' : ''}`}
                            onClick={() => handleWeekdaySelection(key)}
                          >
                            {label}
                            {hasSelectedTimes && <span className="time-count">({selectedTimes[key].length})</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedWeekday && (
                    <div className="time-selection-for-day">
                      <div className="time-slots">
                        {TIME_SLOTS
                          .filter(time => !isLastSlot(time))
                          .map(time => {
                            const isBusy = isTimeSlotBusy(time, selectedWeekday);
                            const canMerge = canMergeWithNext(time, selectedWeekday);
                            const isDisabled = isBusy || !canMerge;
                            const isSelected = selectedTimes[selectedWeekday]?.includes(time);

                            // 종료 시간 계산 (1시간 후)
                            const [hour, min] = time.split(':').map(Number);
                            const endTime = `${String(hour + 1).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

                            return (
                              <button
                                key={`${selectedWeekday}-${time}`}
                                type="button"
                                className={`time-slot ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={() => !isDisabled && handleTimeSelection(selectedWeekday, time)}
                                disabled={isDisabled}
                                title={isBusy ? t('time_already_reserved') : !canMerge ? t('one_hour_lesson_unavailable') : t('one_hour_lesson_slot', { start: time, end: endTime })}
                              >
                                {time}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {formatSelectedTimes() && (
                    <div className="selected-times-preview">
                      <strong>{t('selected_times')}</strong>
                      <p>{formatSelectedTimes()}</p>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">{t('additional_message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows={4}
                    placeholder={t('message_placeholder_lesson')}
                  />
                </div>
              </div>

              {/* STEP 3: 확인 및 제출 */}
              <div className="form-section confirmation-section" data-step="3">
                <div className="section-header">
                  <div className="section-label">STEP 3</div>
                  <h2 className="section-title">{t('step3_title')}</h2>
                  <p className="section-description">{t('step3_description')}</p>
                </div>

                {/* 동의 체크박스 */}
                <div className="agreement-section">
                  <label className="checkbox-label agreement-checkbox">
                    <input
                      type="checkbox"
                      checked={isPolicyAgreed}
                      onChange={(e) => setIsPolicyAgreed(e.target.checked)}
                    />
                    <span className="checkbox-text">{t('lesson_policy_agree_all')}</span>
                  </label>

                  {/* 만 14세 미만: 법정대리인(보호자) 동의 */}
                  {requiresGuardianConsent && (
                    <>
                      <p className="guardian-consent-notice">{t('guardian_consent_notice')}</p>
                      <label className="checkbox-label agreement-checkbox">
                        <input
                          type="checkbox"
                          checked={isGuardianAgreed}
                          onChange={(e) => setIsGuardianAgreed(e.target.checked)}
                        />
                        <span className="checkbox-text">{t('guardian_consent_label')}</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  disabled={isSubmitting || !isPolicyAgreed || (requiresGuardianConsent && !isGuardianAgreed)}
                  className="btn btn-primary btn-md"
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {t('loading')}
                    </>
                  ) : (
                    t('apply_lesson')
                  )}
                </button>
              </div>
            </form>

            <aside className="lr-side">
              <div className="side-panel">
                <div className="side-panel-header">{t('lesson_pricing')}</div>
                <div className="side-pricing-row">
                  <div>
                    <div className="side-pricing-name">{t('home_program_regular_title')}</div>
                    <div className="side-pricing-meta">{t('lr_pricing_meta')}</div>
                  </div>
                  <div className="side-pricing-price">{t('home_program_regular_price')}</div>
                </div>
                <div className="side-panel-note">{t('lr_pricing_note')}</div>
              </div>

              <div className="side-panel side-panel-plain">
                <div className="side-panel-title">{t('lr_process_title')}</div>
                <ol className="side-steps">
                  <li>{t('lr_process_1')}</li>
                  <li>{t('lr_process_2')}</li>
                  <li>{t('lr_process_3')}</li>
                  <li>{t('lr_process_4')}</li>
                </ol>
              </div>

              <div className="side-panel side-panel-plain">
                <div className="side-panel-title">{t('lesson_policy_title')}</div>
                <ul className="policy-list side-policy-list">
                  <li className="policy-item">{t('lesson_policy_cancellation')}</li>
                  <li className="policy-item">{t('lesson_policy_practice_room')}</li>
                  <li className="policy-item">{t('lesson_policy_inquiry')}</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonRequest;